import { useRef, useEffect, useCallback } from 'react';

/**
 * usePerformanceMonitoring Hook
 * 
 * Tracks render performance, memory usage, and provides cleanup utilities.
 * Monitors render times, memory usage, and provides performance metrics.
 * 
 * @returns {Object} Performance monitoring utilities
 */
const usePerformanceMonitoring = () => {
  // ⚡️ PERFORMANCE MONITORING: Track render performance and memory usage
  const performanceMetrics = useRef({
    renderCount: 0,
    lastRenderTime: 0,
    averageRenderTime: 0,
    memoryUsage: 0,
    messageCount: 0,
    startTime: 0
  });

  // ⚡️ PERFORMANCE MONITORING: Update metrics on each render
  const trackRenderPerformance = useCallback((messageCount = 0) => {
    const startTime = performance.now();
    performanceMetrics.current.startTime = startTime;
    
    return () => {
      const endTime = performance.now();
      const renderTime = endTime - startTime;
      
      performanceMetrics.current.renderCount++;
      performanceMetrics.current.lastRenderTime = renderTime;
      performanceMetrics.current.averageRenderTime = 
        (performanceMetrics.current.averageRenderTime * (performanceMetrics.current.renderCount - 1) + renderTime) / 
        performanceMetrics.current.renderCount;
      performanceMetrics.current.messageCount = messageCount;
      
      // Log performance warnings if render time is too high
      if (renderTime > 16) { // More than 16ms (60fps threshold)
      }
      
      // Log memory usage periodically
      if (performanceMetrics.current.renderCount % 100 === 0) {
        if (performance.memory) {
          performanceMetrics.current.memoryUsage = performance.memory.usedJSHeapSize;
        }
      }
    };
  }, []);

  // ⚡️ MEMORY LEAK PREVENTION: Comprehensive cleanup utility
  const performMemoryCleanup = useCallback(() => {
    const startTime = performance.now();
    
    // Clean up deduplication cache
    if (window.chatCache) {
      window.chatCache.clear();
    }
    
    // Clean up offline storage
    if (window.offlineStorage) {
      window.offlineStorage.cleanup();
    }
    
    // Force garbage collection if available (Chrome DevTools)
    if (window.gc) {
      window.gc();
    }
    
    const endTime = performance.now();
    
    return endTime - startTime;
  }, []);

  // ⚡️ PERFORMANCE MONITORING: Get current metrics
  const getPerformanceMetrics = useCallback(() => {
    return {
      ...performanceMetrics.current,
      memoryInfo: performance.memory ? {
        used: performance.memory.usedJSHeapSize,
        total: performance.memory.totalJSHeapSize,
        limit: performance.memory.jsHeapSizeLimit
      } : null
    };
  }, []);

  // ⚡️ PERFORMANCE MONITORING: Reset metrics
  const resetPerformanceMetrics = useCallback(() => {
    performanceMetrics.current = {
      renderCount: 0,
      lastRenderTime: 0,
      averageRenderTime: 0,
      memoryUsage: 0,
      messageCount: 0,
      startTime: 0
    };
  }, []);

  // ⚡️ PERFORMANCE MONITORING: Check if performance is degraded
  const isPerformanceDegraded = useCallback(() => {
    return (
      performanceMetrics.current.averageRenderTime > 16 || // More than 16ms average
      performanceMetrics.current.lastRenderTime > 50 || // More than 50ms last render
      (performance.memory && performance.memory.usedJSHeapSize > performance.memory.jsHeapSizeLimit * 0.8) // More than 80% memory usage
    );
  }, []);

  // ⚡️ PERFORMANCE MONITORING: Get performance recommendations
  const getPerformanceRecommendations = useCallback(() => {
    const recommendations = [];
    
    if (performanceMetrics.current.averageRenderTime > 16) {
      recommendations.push('Consider using React.memo() for expensive components');
    }
    
    if (performanceMetrics.current.messageCount > 1000) {
      recommendations.push('Consider implementing virtualization for large message lists');
    }
    
    if (performance.memory && performance.memory.usedJSHeapSize > performance.memory.jsHeapSizeLimit * 0.7) {
      recommendations.push('Consider implementing message pagination or cleanup');
    }
    
    return recommendations;
  }, []);

  return {
    trackRenderPerformance,
    performMemoryCleanup,
    getPerformanceMetrics,
    resetPerformanceMetrics,
    isPerformanceDegraded,
    getPerformanceRecommendations
  };
};

export default usePerformanceMonitoring;
